/**
 * OverviewTab — read-only combined activity feed (COGS push log + SIT reclass
 * log + sync log), grouped by day with sticky day headers, plus a right rail
 * carrying a connection-health card, a reconciliation hint, and the AI invoice
 * ingestion toggle. No primary action on this tab.
 */
import { BookOpenCheck, RefreshCw, Ship } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import type { RouterOutputs } from '@/lib/trpc';
import { trpc } from '@/lib/trpc';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { cn } from '@/lib/utils';
import { JournalLink, JournalStatusBadge } from '../shared';
import { InvoiceAiCard } from '../InvoiceAiCard';
import { ConnectionPill } from '../ConnectionPill';
import { connectionPill, type XeroStatus } from '../../lib/connection';
import {
  buildActivityFeed,
  groupByDay,
  type ActivityEntry,
  type ActivityKind,
} from '../../lib/activity';
import { formatDayHeader, formatRelative } from '../../lib/format';

type CogsStats = RouterOutputs['xero']['getCogsStats'];
type SitStats = RouterOutputs['xero']['getSitStats'];

const KIND_ICON: Record<ActivityKind, typeof BookOpenCheck> = {
  cogs: BookOpenCheck,
  sit: Ship,
  sync: RefreshCw,
};

function ActivityRow({ entry }: { entry: ActivityEntry }) {
  const { format } = useCurrencyFormatter();
  const Icon = KIND_ICON[entry.kind];
  return (
    <div className="flex items-start gap-3 px-3 py-2.5">
      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted">
        <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <p className="text-sm font-medium">{entry.description}</p>
          {entry.isReversal && (
            <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Reversal
            </span>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
          <span>{formatRelative(entry.at)}</span>
          {entry.journalId && (
            <>
              <span aria-hidden="true">·</span>
              <JournalLink journalId={entry.journalId} journalNumber={entry.journalNumber} />
            </>
          )}
        </div>
        {entry.status === 'failed' && entry.errorMessage && (
          <p className="mt-0.5 truncate text-[11px] text-destructive" title={entry.errorMessage}>
            {entry.errorMessage}
          </p>
        )}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        {entry.amount != null && (
          <span className="text-sm font-medium tabular-nums">{format(entry.amount, 'AUD')}</span>
        )}
        <JournalStatusBadge status={entry.status} />
      </div>
    </div>
  );
}

export function OverviewTab({
  status,
  cogsStats,
  sitStats,
  postedAmount,
  isAdmin,
}: {
  status: XeroStatus;
  cogsStats: CogsStats | undefined;
  sitStats: SitStats | undefined;
  postedAmount: number | null;
  isAdmin: boolean;
}) {
  const { format } = useCurrencyFormatter();
  const cogsLogQuery = trpc.xero.getCogsLog.useQuery({ limit: 20 }, { staleTime: 30_000 });
  const sitLogQuery = trpc.xero.getSitLog.useQuery({ limit: 20 }, { staleTime: 30_000 });
  const syncLogQuery = trpc.xero.getSyncLogs.useQuery({ limit: 20 }, { staleTime: 30_000 });

  const loading =
    cogsLogQuery.isLoading || sitLogQuery.isLoading || syncLogQuery.isLoading;

  const days = groupByDay(
    buildActivityFeed(cogsLogQuery.data, sitLogQuery.data, syncLogQuery.data)
  );

  const failed = (cogsStats?.totalFailed ?? 0) + (sitStats?.failedCount ?? 0);

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
      {/* Activity feed */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Activity</CardTitle>
          <CardDescription>
            Every COGS journal, stock-in-transit reclass, and account sync, newest first.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <p className="px-6 py-8 text-center text-sm text-muted-foreground">
              Loading activity…
            </p>
          ) : days.length === 0 ? (
            <p className="px-6 py-8 text-center text-sm text-muted-foreground">
              No Xero activity yet. Journals and syncs will appear here as they post.
            </p>
          ) : (
            <div>
              {days.map((day) => (
                <div key={day.key}>
                  <div className="sticky top-0 z-10 border-y bg-muted/60 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground backdrop-blur supports-[backdrop-filter]:bg-muted/40">
                    {formatDayHeader(day.at)}
                  </div>
                  <div className="divide-y">
                    {day.entries.map((entry) => (
                      <ActivityRow key={entry.id} entry={entry} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Right rail */}
      <div className="space-y-4">
        {/* Connection health */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Connection health</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Status</span>
              <ConnectionPill state={connectionPill(status)} />
            </div>
            {status.tenantName && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">Organisation</span>
                <span className="truncate font-medium">{status.tenantName}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Last push</span>
              <span className="tabular-nums">
                {formatRelative(
                  [cogsStats?.lastPushAt, sitStats?.lastPushAt]
                    .filter((value): value is string => Boolean(value))
                    .sort()
                    .at(-1) ?? null
                )}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Failed journals</span>
              <span
                className={cn(
                  'tabular-nums font-medium',
                  failed > 0 ? 'text-amber-700' : 'text-emerald-700'
                )}
              >
                {failed}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Reconciliation hint */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Reconciliation</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            {postedAmount !== null && postedAmount > 0 ? (
              <p>
                <span className="font-medium text-foreground">{format(postedAmount, 'AUD')}</span>{' '}
                has posted to Xero across COGS and stock-in-transit journals. Check this against the
                matching account balances in Xero to confirm your books reconcile.
              </p>
            ) : (
              <p>
                Once journals start posting, the running posted total appears here so you can spot
                check it against your Xero balances.
              </p>
            )}
          </CardContent>
        </Card>

        {/* AI invoice ingestion */}
        <InvoiceAiCard isAdmin={isAdmin} />
      </div>
    </div>
  );
}
